import { AlertTriangle, RefreshCw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode, useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Disclosure } from "@/components/ui/disclosure";
import { pageErrorClass } from "@/components/ui/atoms";
import { pageStateClass } from "@/components/ui/chrome";
import { cn } from "@/lib/utils";

/**
 * A rede embaixo do app (TUT-206a).
 *
 * **O defeito que ela existe para pegar.** Em 21/08 o dono relatou
 * `Minified React error #185` mexendo no Kanban. O erro apagou a tela inteira
 * e **não deixou rastro**: o app não tinha nenhum limite de erro, então
 * qualquer exceção de render desmontava a árvore e o que sobrava era uma
 * página em branco. Sem componente que sobreviva ao erro, não há onde ler o
 * que aconteceu — e um defeito que só se manifesta como tela branca é um
 * defeito que se investiga por adivinhação.
 *
 * **O que ela entrega, além de não quebrar a cara.** A mensagem e a pilha de
 * componentes ficam VISÍVEIS na tela, dentro de um `<details>`, prontas para
 * copiar. Junto com os mapas de origem ligados no build, isso transforma
 * "deu erro no Kanban" em um endereço de arquivo e linha.
 *
 * **Por que classe.** Não existe equivalente em hook: `componentDidCatch` e
 * `getDerivedStateFromError` só existem em componente de classe. É a única
 * classe do produto, e é por imposição do React.
 */

/**
 * TUT-421 — o detalhe técnico nasce ABERTO quando quem olha é um navegador
 * automatizado (`navigator.webdriver`, o que a Playwright liga).
 *
 * Na CI do #744, a e2e caiu uma vez nesta tela logo depois do login, e o
 * diagnóstico só mostrou "Esta tela parou de responder": o snapshot da
 * Playwright lê a árvore acessível, e o detalhe fechado não está nela. A
 * falha passou na repetição e não se reproduziu em 66 tentativas locais, ou
 * seja, a causa ficou sem endereço. Aberto, a mensagem e a pilha entram no
 * `error-context.md`, e o próximo vermelho já diz onde quebrou. Para a pessoa
 * no navegador de verdade nada muda: `webdriver` é falso.
 */
export function detalhesAbertosPorPadrao(): boolean {
  return typeof navigator !== "undefined" && navigator.webdriver === true;
}

type Props = { children: ReactNode };
type State = { erro: Error | null; pilha: string; detalhes: boolean };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { erro: null, pilha: "", detalhes: detalhesAbertosPorPadrao() };

  static getDerivedStateFromError(erro: Error): Partial<State> {
    return { erro };
  }

  override componentDidCatch(erro: Error, info: ErrorInfo) {
    // Prefixo estável para quem for procurar no console de um navegador de
    // verdade: `TUTELAE_CRASH` é grepável e não colide com ruído de terceiros.
    console.error("TUTELAE_CRASH", erro, info.componentStack);
    this.setState({ pilha: info.componentStack ?? "" });
  }

  override render() {
    const { erro, pilha, detalhes } = this.state;
    if (!erro) return this.props.children;
    return (
      <TelaDeErro
        erro={erro}
        pilha={pilha}
        detalhes={detalhes}
        aoAlternarDetalhes={(aberto) => this.setState({ detalhes: aberto })}
      />
    );
  }
}

/**
 * A tela de erro da casa, sem estado — a MESMA para a rede da raiz
 * (`ErrorBoundary`, fora do roteador) e para a fronteira por rota
 * (`TelaDeErroDaRota`, TUT-392).
 */
export function TelaDeErro({
  erro,
  pilha,
  detalhes,
  aoAlternarDetalhes,
}: {
  erro: Error;
  pilha: string;
  detalhes: boolean;
  aoAlternarDetalhes: (aberto: boolean) => void;
}) {
  return (
      <div className={cn(pageStateClass, pageErrorClass)} role="alert">
        <AlertTriangle aria-hidden="true" />
        <strong>Esta tela parou de responder</strong>
        <p>
          O erro foi contido aqui — o resto do sistema segue de pé, e nada do
          que você salvou se perdeu. Recarregar costuma resolver.
        </p>
        <Button
          variant="vidro"
          type="button"
          onClick={() => window.location.reload()}
        >
          <RefreshCw aria-hidden="true" />
          Recarregar
        </Button>
        {/* O detalhe técnico fica FECHADO por padrão e legível quando aberto:
            quem só quer voltar a trabalhar não tropeça nele, e quem precisa
            relatar tem o que copiar sem abrir o console.

            `Disclosure`, e não `<details>` cru: o acervo de `<details>` nativo
            é fechado e decrescente (`popover-pattern.test.ts`), e a primitiva
            já resolve marcador, alvo de toque de uma linha e o giro da seta
            que respeita `prefers-reduced-motion`. */}
        <Disclosure
          className="mt-3 w-full max-w-[46rem] text-left"
          open={detalhes}
          onOpenChange={aoAlternarDetalhes}
          summary={
            <span className="text-legenda font-semibold text-[var(--muted)]">
              Detalhes técnicos (para relatar o problema)
            </span>
          }
        >
          <pre className="max-h-[16rem] overflow-auto whitespace-pre-wrap rounded-md border border-[var(--border)] bg-[var(--surface-2)] p-3 text-legenda text-[var(--text)]">
            {erro.message}
            {pilha}
          </pre>
        </Disclosure>
      </div>
  );
}

/**
 * TUT-392 — a fronteira de erro POR ROTA, ligada como `defaultErrorComponent`
 * do roteador.
 *
 * **O defeito.** O `ErrorBoundary` acima fica FORA do roteador (TUT-206a) — e
 * por isso nunca era alcançado: o TanStack Router envolve cada rota numa
 * fronteira própria, e sem `errorComponent` ela mostra o padrão da biblioteca,
 * um "Something went wrong!" em inglês, no lugar da rota inteira. É a
 * causa-raiz do crash do portal de 18/08, e seguia valendo para qualquer tela.
 *
 * **O que muda.** O erro fica contido na ROTA que quebrou: a casca (lateral,
 * cabeçalho) segue de pé, a mensagem é a da casa, em português, com o detalhe
 * técnico para relatar, e o prefixo `TUTELAE_CRASH` no console.
 */
export function TelaDeErroDaRota({ error }: { error: unknown }) {
  // Memo pelo erro recebido: sem ele, um valor que não é `Error` viraria um
  // `Error` NOVO a cada render, e o efeito abaixo registraria o crash de novo.
  const erro = useMemo(
    () => (error instanceof Error ? error : new Error(String(error))),
    [error],
  );
  const [detalhes, setDetalhes] = useState(detalhesAbertosPorPadrao);
  useEffect(() => {
    console.error("TUTELAE_CRASH", erro);
  }, [erro]);
  return (
    <TelaDeErro
      erro={erro}
      pilha={erro.stack ?? ""}
      detalhes={detalhes}
      aoAlternarDetalhes={setDetalhes}
    />
  );
}
